import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Optional,
  Output,
  SimpleChange,
  SimpleChanges,
  SkipSelf,
  ViewChild,
} from '@angular/core';
import {
  AbstractControl,
  ControlContainer,
  UntypedFormGroup,
} from '@angular/forms';
import { SubSink } from 'subsink';
import { PaymentAccount } from '../../models/payment-accounts/payment-account.model';
import { PaymentAccountStatus } from '../../models/payment-accounts/payment-account-status.enum';
import { AccountPaymentMethod, SelectedPaymentAccount } from '../../models';
import { ConfirmationComponent, ErrorMessage } from '@arc/aureus';
import { TranslateService } from '@ngx-translate/core';

interface PaymentAccountSelectorChanges extends SimpleChanges {
  selectedPaymentAccount?: SimpleChange;
  numberOfDocumentsNotPayableWithPaymentMethod?: SimpleChange;
}

@Component({
  selector: 'payment-account-selector',
  templateUrl: './payment-account-selector.component.html',
  styleUrls: ['./payment-account-selector.component.scss'],
})
export class PaymentAccountSelectorComponent
  implements OnInit, OnChanges, OnDestroy
{
  @Input() hasPaymentAccounts: boolean;
  @Input() paymentAccounts: PaymentAccount[];
  @Input() selectedPaymentAccount: SelectedPaymentAccount;
  @Input() showDefaultPaymentAccount: boolean;
  @Input() showDiscountsExpiredForNewDateWarning: boolean;
  @Input() showPaymentMethodNotDiscountableWarning: boolean;
  @Input() controlName: string;
  @Input() showMultipleAccountWarning: boolean;
  @Input() showAchPaymentDateWarning: boolean;
  @Input() achPaymentDateWarningVerbiage: string;
  @Input() showSurchargeVerbiage: boolean;
  @Input() surchargeAuthVerbiageText: string;
  @Input() showUpdateExpirationDateInput: boolean;
  @Input() allowPaymentAccountsDeletion: boolean;
  @Input() showDiscountLostPayableAmountUpdatedWarning: boolean;
  @Input() showSomeDocumentsNotPayableWarning: boolean;
  @Input() numberOfDocumentsNotPayableWithPaymentMethod: number;
  @Input() showPadVerifiedBadges: boolean;
  @Input() isCanadianPadRequiredFlow: boolean;

  @Output() deletePaymentAccount = new EventEmitter<string>();

  @ViewChild('deletePaymentAccountConfirmation')
  deletePaymentAccountConfirmation: ConfirmationComponent;

  defaultPaymentAccount: PaymentAccount;
  paymentAccountStatus = PaymentAccountStatus;
  accountPaymentMethod = AccountPaymentMethod;
  someDocumentsNotPayableWarningText: string;
  control: AbstractControl;
  form: UntypedFormGroup;

  paymentAccountStatusTranslations = {
    Pending: 'ARC_PAYMENT_PENDING_STATUS',
    Suspended: 'ARC_PAYMENT_SUSPENDED_STATUS',
    Cancelled: 'ARC_PAYMENT_CANCELLED_STATUS',
  };

  futureExpirationDateErrorMessage: ErrorMessage = {
    message: (param: { startYear: string; endYear: string }) =>
      `${
        this.translateService.instant(
          'AUREUS_CREDIT_CARD_INPUT_FUTURE_DATE_ERROR',
          { startYear: param.startYear, endYear: param.endYear },
        ) as string
      }`,
    param: 'param',
  };

  private paymentAccountBurnToDelete: string;
  private subsink = new SubSink();

  constructor(
    @Optional()
    @SkipSelf()
    private controlContainer: ControlContainer,
    private translateService: TranslateService,
  ) {}

  ngOnInit() {
    this.control = this.controlContainer.control.get(this.controlName);
    this.form = this.controlContainer.control as UntypedFormGroup;

    this.defaultPaymentAccount = this.getDefaultPaymentAccount();
    if (
      this.defaultPaymentAccount != undefined &&
      this.showDefaultPaymentAccount
    ) {
      this.control.setValue(this.defaultPaymentAccount);
    }

    this.showBadge();
  }

  ngOnChanges(changes: PaymentAccountSelectorChanges): void {
    if (
      (changes.selectedPaymentAccount &&
        changes.selectedPaymentAccount.currentValue) ||
      (changes.numberOfDocumentsNotPayableWithPaymentMethod &&
        changes.numberOfDocumentsNotPayableWithPaymentMethod.currentValue)
    ) {
      if (
        this.selectedPaymentAccount &&
        this.numberOfDocumentsNotPayableWithPaymentMethod > 0
      )
        this.someDocumentsNotPayableWarningText =
          this.selectedPaymentAccount.paymentMethod == AccountPaymentMethod.Ach
            ? (this.translateService.instant(
                'ARC_PAYMENT_SOME_DOCUMENTS_NOT_PAYABLE_WITH_ACH',
                {
                  numberOfDocuments:
                    this.numberOfDocumentsNotPayableWithPaymentMethod.toString(),
                },
              ) as string)
            : (this.translateService.instant(
                'ARC_PAYMENT_SOME_DOCUMENTS_NOT_PAYABLE_WITH_CARD',
                {
                  numberOfDocuments:
                    this.numberOfDocumentsNotPayableWithPaymentMethod.toString(),
                },
              ) as string);
    }
  }

  ngOnDestroy(): void {
    this.subsink.unsubscribe();
  }

  getDefaultPaymentAccount(): PaymentAccount {
    const selectedAccount = this.paymentAccounts.find(
      (account) =>
        account.paymentAccountIdentifierBurn ===
        this.selectedPaymentAccount?.burn,
    );

    if (selectedAccount) {
      return selectedAccount;
    }

    const firstActiveAccount = this.paymentAccounts.find(
      (account) => account.accountStatus == PaymentAccountStatus.Active,
    );

    if (firstActiveAccount) {
      return firstActiveAccount;
    }

    const firstNoStatusAccount = this.paymentAccounts.find(
      (account) => account.accountStatus == null,
    );

    return firstNoStatusAccount;
  }

  onDeletePaymentAccountClicked(account: PaymentAccount) {
    this.paymentAccountBurnToDelete = account.paymentAccountIdentifierBurn;
    this.deletePaymentAccountConfirmation.confirm();
  }

  confirmPaymentAccountDeletion() {
    this.deletePaymentAccount.emit(this.paymentAccountBurnToDelete);
  }

  private showBadge() {
    this.paymentAccounts.forEach((account) => {
      account.showBadge =
        account.accountStatus == PaymentAccountStatus.Pending ||
        account.accountStatus == PaymentAccountStatus.Suspended ||
        account.accountStatus == PaymentAccountStatus.Cancelled;
    });
  }

  getAccountSubText(account: PaymentAccount): string {
    if (account.paymentMethod == AccountPaymentMethod.Card) {
      return `${account.expirationMonth}/${account.expirationYear
        .toString()
        .slice(-2)}`;
    } else if (account.displayName.includes('BANK')) {
      return account.bankName;
    } else {
      return `${account.bankName} - ${account.accountLast4}`;
    }
  }

  isPadVerified(account: PaymentAccount): boolean {
    return this.isCanadianPadRequiredFlow
      ? account.padVerifiedStatus?.padVerified &&
          !account.padVerifiedStatus?.padSignedSeparately
      : account.padVerifiedStatus?.padVerified;
  }
}
